import { useEffect, useState } from "react";
import { useAccountContext } from "./useAccountContext";
import { useAuthContext } from "./useAuthContext";
import { useSettingsContext } from "./useSettingsContext";
import axios from "axios";
import { v4 as uuidv4 } from 'uuid';

export const useAnalytics = () => {
    const {
        username,
        user,
        clientApi,
        dispatch: authDispatch,
    } = useAuthContext();
    const [isLoading, setIsLoading] = useState(false);
    const { account } = useAccountContext();
    const { isWinAuth } = useSettingsContext();

    const axiosInstance = axios.create({
        baseURL: `${clientApi}`,
        headers: {
            accepts: "application/json",
            "Content-Type": "application/json",
        },
    });

    axiosInstance.interceptors.request.use(
        (request) => {

            const accessToken = user.token;
            if (accessToken) {
                request.headers["Authorization"] = `Bearer ${accessToken}`;
            }
            return request;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    axiosInstance.interceptors.response.use(
        response => response, // Directly return successful responses.
        async error => {
            const originalRequest = error.config;
            if (error.response.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true; // Mark the request as retried to avoid infinite loops.
                try {
                    if (!isWinAuth) {
                        // Make a request to your auth server to refresh the token.
                        const response = await axiosInstance.post(`${clientApi}api/auth/refresh`, user);
                        // Store the new access and refresh tokens.
                        authDispatch({ type: 'Refresh', payload: response.data, clientApi: clientApi, username: username });

                        // Update the authorization header with the new access token.
                        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                    }
                    else {
                        const newToken = await acquireSilentToken();
                        let tmpUser = user;
                        tmpUser.token = newToken;
                        authDispatch({ type: 'REFRESH', payload: tmpUser, clientApi: clientApi, username: username });
                        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                    }
                    return axios(originalRequest); // Retry the original request with the new access token.
                } catch (refreshError) {
                    // Handle refresh token errors by clearing stored tokens and redirecting to the login page.
                    console.error("Token refresh failed:", refreshError);
                    authDispatch({ type: "LOGOUT" });
                    return Promise.reject(refreshError);
                }
            }
            return Promise.reject(error); // For all other errors, return the error as is.
        }
    );

    // GET /api/Analytics/Employee/Distribution/{company}?By=department|location|seniority|nationality
    const getEmployeeBreakdown = async (company, groupBy) => {
        setIsLoading(true);
        try {
            const res = await axiosInstance.get(`/api/Analytics/Employee/Distribution/${company}`, {
                params: { By: groupBy },
            });
            return res.data; // expected: [{ label, value }] (or Label/Value depending on serializer)
        } finally {
            setIsLoading(false);
        }
    };

    // GET /api/Analytics/Employee/Aggregations/{company}
    const getEmployeeAggregations = async (company) => {
        setIsLoading(true);
        try {
            const res = await axiosInstance.get(`/api/Analytics/Employee/Aggregations/${company}`);
            return res.data;
            // expected:
            // { totalEmployees: number, averageAge: number|null, averageWorkingYears: number|null }
        } finally {
            setIsLoading(false);
        }
    };

    // GET /api/Analytics/Employee/AgeRanges/{company}
    const getEmployeesAgeRange = async (company) => {
        setIsLoading(true);
        try {
            const res = await axiosInstance.get(`/api/Analytics/Employee/AgeRanges/${company}`);
            return res.data; // expected: [{ label, value }]
        } finally {
            setIsLoading(false);
        }
    };

    // GET /api/Analytics/Employee/TenureRanges/{company}
    const getEmployeesTenureRange = async (company) => {
        setIsLoading(true);
        try {
            const res = await axiosInstance.get(`/api/Analytics/Employee/TenureRanges/${company}`);
            return res.data; // expected: [{ label, value }]
        } finally {
            setIsLoading(false);
        }
    };

    // GET /api/Analytics/Employee/AttritionRate/{company}?year=2017
    const getEmployeeAttrition = async (company, year) => {
        setIsLoading(true);
        try {
            const res = await axiosInstance.get(`/api/Analytics/Employee/AttritionRate/${company}`, {
                params: { year },
            });
            return res.data;
            // expected:
            // { attritionRatePct: number, leavers: number, avgHeadcount: number }
        } finally {
            setIsLoading(false);
        }
    };

    // GET /api/Analytics/Employee/EmploymentTypes/{company}
    const getEmployeesEmploymentTypes = async (company) => {
        setIsLoading(true);
        try {
            const res = await axiosInstance.get(`/api/Analytics/Employee/EmploymentTypes/${company}`);
            return res.data; // expected: [{ label, value }]
        } finally {
            setIsLoading(false);
        }
    };

    // GET /api/Analytics/Employee/QualificationsBar/{company}
    const getEmployeesQualificationTypes = async (company) => {
        setIsLoading(true);
        try {
            const res = await axiosInstance.get(`/api/Analytics/Employee/Qualifications/${company}`);
            return res.data; // expected: [{ label, value }]
        } finally {
            setIsLoading(false);
        }
    };

    // GET /api/Analytics/Leave/RequestsPerMonth/{company}?year=2017
    const getApprovedLeaveRequestsPerMonth = async (company, year) => {
        setIsLoading(true);
        try {
            const res = await axiosInstance.get(`/api/Analytics/Leave/RequestsPerMonth/${company}`, {
                params: { year },
            });
            return res.data; // expected: [{ label, value }]
        } finally {
            setIsLoading(false);
        }
    };

    // GET /api/Analytics/Leave/RequestsPerDay/{company}?year=2017&month=3
    const getApprovedLeaveRequestsPerDay = async (company, year, month) => {
        setIsLoading(true);
        try {
            const res = await axiosInstance.get(`/api/Analytics/Leave/RequestsPerDay/${company}`, {
                params: { year, month },
            });
            return res.data; // expected: [{ label, value }]
        } finally {
            setIsLoading(false);
        }
    };

     // GET /api/Analytics/Leave/DetailsPerDay/{company}?date=2025-08-03
    const getApprovedLeaveDetailsPerDay = async (company, date) => {
        setIsLoading(true);
        try {
            const res = await axiosInstance.get(`/api/Analytics/Leave/DetailsPerDay/${company}`, {
                params: { date },
            });
            return res.data;
        } finally {
            setIsLoading(false);
        }
    };




    return {
        isLoading,
        getEmployeeBreakdown,
        getEmployeeAggregations,
        getEmployeesAgeRange,
        getEmployeesTenureRange,
        getEmployeeAttrition,
        getEmployeesEmploymentTypes,
        getEmployeesQualificationTypes,
        getApprovedLeaveRequestsPerMonth,
        getApprovedLeaveRequestsPerDay,
        getApprovedLeaveDetailsPerDay
    };

};